import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, Stack, TextField } from '@mui/material';
import { t } from 'i18next';
import { ChangeEvent, PureComponent } from 'react';
import { bind } from '../../utils/decorators';

type PasswordName = 'current-password' | 'new-password' | 'confirm-password';

type Props = {
  name: PasswordName;
  label: string;
  focusedLabel?: string;
  disabled: boolean;
  compareTo?: string | null;
  autofocus?: true;
  onChange: (value: string | null) => void;
};

type State = {
  value: string;
  visible: boolean;
  focused: boolean;
  dirty: boolean;
  error: string | null;
};

/**
 * The password must be at least 8 characters long, without leading and trailing whitespaces.
 * The API trims them, it's important that the user knows and adjusts.
 */
const PASSWORD_REGEX = /^(?=.{8,}$)\S.*\S$/;

/**
 * Map the password type to `autocomplete` fields to enable browser suggestions
 */
const AUTOCOMPLETE_MAP = {
  'current-password': 'current-password',
  'new-password': 'new-password',
  'confirm-password': 'new-password',
};

export class PasswordInput extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { visible: false, error: null, value: '', focused: props.autofocus ?? false, dirty: false };
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (this.props.name === 'confirm-password' && prevProps.compareTo !== this.props.compareTo) {
      this.processChange();
    }
  }

  render() {
    const { name, label, focusedLabel, disabled, autofocus } = this.props;
    const { value, visible, focused, error, dirty } = this.state;
    const showError = error !== null && dirty && !focused;

    return (
      <Stack direction="row" spacing={1} alignItems="end" sx={{ '& .MuiTextField-root': { flexGrow: 1 }, '& .MuiIconButton-root': showError ? { mb: 3 } : {} }}>
        <TextField
          name={name}
          value={value}
          label={focused || value ? focusedLabel ?? label : label}
          helperText={showError ? error : null}
          error={showError}
          disabled={disabled}
          variant="standard"
          type={visible ? 'text' : 'password'}
          autoComplete={AUTOCOMPLETE_MAP[name]}
          spellCheck="false"
          autoCapitalize="false"
          autoCorrect="false"
          multiline={false}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          focused={focused}
          autoFocus={autofocus}
          required={true}
          size="medium"
          inputProps={{ 'aria-autocomplete': undefined }}
        />
        <IconButton
          data-role="password-toggle"
          size="small"
          title={t(visible ? 'togglePasswordVisibility.visible' : 'togglePasswordVisibility.hidden')}
          disabled={!value}
          onClick={this.togglePassword}
        >
          {visible ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </Stack>
    );
  }

  @bind
  private onFocus() {
    this.setState({ focused: true });
  }

  @bind
  private onBlur() {
    this.setState({ focused: false });
  }

  @bind
  private onChange(event: ChangeEvent<HTMLInputElement>) {
    this.setState({ value: event.target.value, dirty: true }, () => this.processChange());
  }

  @bind
  private togglePassword() {
    this.setState((prev) => ({ ...prev, visible: !prev.visible }));
  }

  @bind
  private processChange() {
    if (this.passwordIsValid()) {
      this.setState({ error: null }, () => this.props.onChange(this.state.value || null));
    } else {
      this.setState({ error: this.getError() }, () => this.props.onChange(null));
    }
  }

  @bind
  private passwordIsValid() {
    switch (this.props.name) {
      case 'current-password':
        // The current password is always valid, we don't know what the users had before the length policy enforcement
        return this.state.value.length > 0;
      case 'new-password':
        // The new password must match the policy
        return PASSWORD_REGEX.test(this.state.value);
      case 'confirm-password':
        // The confirmation must be equal to the new password, if the new password is set
        return this.props.compareTo === null || this.props.compareTo === '' || this.props.compareTo === this.state.value;
    }
  }

  @bind
  private getError() {
    switch (this.props.name) {
      case 'current-password':
        return t('passwordInput.required');
      case 'new-password':
        return t('passwordInput.regex');
      case 'confirm-password':
        return t('passwordInput.notEqual');
    }
  }
}
